import {find, isArray, isString} from 'lodash-es';
import {eventBus} from '../common/event-bus';
import {APP_EMBEDDED} from '../../app.const';
import {getStrings} from '../lang';
import errorsStrings from './errors.strings.yaml';
import {Modal} from 'ant-design-vue';
import {h} from 'vue';

export interface IHttpRequestOptions extends RequestInit {
    headers?: {[key: string]: string};
    params?: any;
    transformResponse?: Function;
    transformRequest?: Function;
    signal?: AbortSignal;
    sentFile?: boolean;
    disableErrorHandling?: boolean;
}

export interface RequestHandler {
    match: (request: RequestInfo, options: IHttpRequestOptions) => boolean;
    process: (request: RequestInfo, options: IHttpRequestOptions) => Promise<any>;
}

export class HttpService {
    FETCH_ID: number = 0;

    REQUESTS_QUEUE: any = {};

    requestHandlers: RequestHandler[] = [];

    constructor() {}

    public addHandler(
        match: (request: RequestInfo, options: IHttpRequestOptions) => boolean,
        process: (request: RequestInfo, options: IHttpRequestOptions) => Promise<any>
    ): void {
        this.requestHandlers.push({match, process});
    }

    private async handleRequest(request: RequestInfo, options: IHttpRequestOptions) {
        const handler: RequestHandler = this.requestHandlers.find((rh: RequestHandler) => rh.match(request, options));
        if (handler) {
            return handler.process(request, options);
        }
        return null;
    }

    private rejectionHandler: (status: number, response: Response, data?: any[]) => void = async (
        status: number,
        response: Response,
        data?: any[]
    ) => {
        const S: CommonStrings & ErrorsStrings = getStrings(errorsStrings);
        let message = '';
        if (data) {
            message = this.getMessage(data);
        } else if (response) {
            let text = '';
            try {
                text = await response.text();
            } catch (e) {}
            message = this.getMessage(text);
        }
        if (message.length) {
            Modal.error({
                title: S.error,
                content: h('div', h('p', message))
            });
        } else {
            Modal.error({
                title: S.error,
                content: S.serverWrongResponse + ' ' + status
            });
        }
    };

    public onReject(handler: (status: number, response: Response) => void): void {
        this.rejectionHandler = handler;
    }

    static paramsToQuery(params: any, fixArrays: boolean = false): string {
        return Object.keys(params)
            .filter((k: string) => params[k] !== null && params[k] !== undefined)
            .map((k: string): string => {
                if (isArray(params[k]) && fixArrays) {
                    return params[k]
                        .map((p: string) => encodeURIComponent(k) + '[]=' + encodeURIComponent(p))
                        .join('&');
                }
                return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
            })
            .join('&');
    }

    static FORM_DATA_HEADERS = {'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'};

    public getBaseUrl(): string {
        const params = new URLSearchParams(document.location.search);
        if (APP_EMBEDDED()) {
            return params.get('origin');
        }
        return params.get('origin') || document.location.origin;
    }

    private async makeRequest(request: RequestInfo, options: IHttpRequestOptions, raw: boolean = false): Promise<any> {
        const handled = await this.handleRequest(request, options);
        if (handled) {
            return {data: handled};
        }
        let url: RequestInfo = request;
        if (options.params) {
            let query: string = HttpService.paramsToQuery(options.params);
            if (query.length) {
                url += ((url as string).indexOf('?') == -1 ? '?' : '') + query;
            }
        }

        options.credentials = 'include';
        let promise: Promise<Response> = fetch(this.getBaseUrl() + url, options).then((response: Response) => {
            if (response.status == 404 || response.status == 405 || response.status == 500 || response.status == 409) {
                this.rejectionHandler.call(null, response.status, response);
            } else if (response.status >= 300 && response.status < 500) {
                throw response;
            } else if (!options.disableErrorHandling && response.status != 200 && response.status != 204) {
                this.rejectionHandler.call(null, response.status, response);
                throw response;
            }
            return response;
        });
        if (raw) {
            return promise;
        } else if (options.transformResponse) {
            return promise
                .then((response: Response) => response.text())
                .then((body: string) => ({data: options.transformResponse(body)}));
        } else {
            return promise
                .then((response: Response) => response.json().catch(() => ({})))
                .then((data: any) => {
                    if (!options.disableErrorHandling && data && data.errors && data.errors.length) {
                        this.rejectionHandler.call(null, 200, null, data);
                    }
                    return {data};
                });
        }
    }

    public async get(request: RequestInfo, options: IHttpRequestOptions = {}): Promise<any> {
        return this.makeRequest(
            request,
            Object.assign(
                {
                    method: 'GET'
                },
                options
            )
        );
    }

    public async post(request: RequestInfo, body: any, options: IHttpRequestOptions = {}): Promise<any> {
        let postBody: any = body;
        options.method = 'POST';
        if (!options.headers) {
            options.headers = {};
        }
        if (body && !isString(body) && !options.sentFile && !options.headers['Content-Type']) {
            postBody = JSON.stringify(body);
            options.headers['Content-Type'] = 'application/json';
        }
        options.body = postBody;
        return this.makeRequest(request, options);
    }

    public async delete(request: RequestInfo, options: IHttpRequestOptions = {}): Promise<any> {
        return this.makeRequest(
            request,
            Object.assign(
                {
                    method: 'DELETE'
                },
                options
            )
        );
    }

    public async put(request: RequestInfo, body: any, options: IHttpRequestOptions = {}): Promise<any> {
        return this.makeRequest(
            request,
            Object.assign(
                {
                    method: 'PUT',
                    body
                },
                options
            )
        );
    }

    public async head(request: RequestInfo, options: IHttpRequestOptions = {}): Promise<any> {
        return this.makeRequest(
            request,
            Object.assign(
                {
                    method: 'HEAD'
                },
                options
            ),
            true
        );
    }

    getMessage(data: any): string {
        const S: CommonStrings & ErrorsStrings = getStrings(errorsStrings);
        if (isString(data)) {
            try {
                data = JSON.parse(data);
            } catch (e) {
                return S.serverError;
            }
        }
        if (!data) {
            return S.serverError;
        } else if (isString(data)) {
            return S.serverError;
        }
        let messagesData: string[] =
            find(
                [data.errors, data.warnings, data.infos],
                (messagesList: string[]) => messagesList && messagesList.length > 0
            ) || [];
        let messages: string[] = messagesData.map((msg: any) => {
            if (msg.messageKey) {
                return S.format(msg.messageKey, ...msg.params);
            }
            return msg.localizedMessage || msg.message;
        });
        if (!messages.length) {
            messages.push(data.localizedMessage || data.message);
        }
        if (data.causedByException) {
            console.error('Server error', data);
            return S.serverError;
        }
        return messages.join('<br>');
    }
}

export const httpService: HttpService = new HttpService();
